import { ApiError, apiRequest } from './api';
import { getEmailSettings } from './settings';
import { getSupabase } from './supabase/client';
import type {
  SummaryEmailPayload,
  SummaryEmailResult,
  SummaryEmailStatus,
} from '../shared/types';
import { t, uiLanguage } from './language';

/**
 * Toplantı özetini e-postayla gönderir — kendi alan adımızdan, sunucu
 * üzerinden (`POST /api/v1/email/summary`, Resend). Kullanıcının Gmail'i
 * kullanılmıyor; uygulama Google'dan e-posta izni istemiyor.
 *
 * Alıcıları sunucu belirler: "Herkese" toplantının katılımcılarını ve
 * içeriği toplantıdan okur, "Kendime" her zaman hesap adresi (SEC-05).
 * Buradan yalnızca içerik ve tercihler gider.
 */

const MEETING_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isMeetingId(value: unknown): value is string {
  return typeof value === 'string' && MEETING_ID_PATTERN.test(value);
}

async function accountEmail(): Promise<string> {
  const { data } = await getSupabase().auth.getSession();
  return data.session?.user.email ?? '';
}

export async function getSummaryEmailStatus(): Promise<SummaryEmailStatus> {
  const { summaryEmailEnabled, summaryEmailEveryone } = await getEmailSettings();
  const own = await accountEmail();
  return {
    enabled: summaryEmailEnabled,
    everyone: summaryEmailEveryone,
    recipient: own,
    canSend: Boolean(own),
  };
}

/**
 * Özeti gönderir.
 *
 * - "Kendime" (`summaryEmailEnabled`): hesap adresin.
 * - "Herkese" (`summaryEmailEveryone`): toplantının katılımcıları (sunucu okur).
 * - `force` elle gönderimdir ("E-posta Gönder"): ayar kapalıyken bile yalnızca
 *   sana gider.
 */
export async function sendSummaryEmail(
  payload: unknown,
  force = false,
  meetingId?: unknown
): Promise<SummaryEmailResult> {
  const safe = sanitizePayload(payload);
  if (!safe) {
    return { ok: false, reason: 'network', message: t('Gönderilecek özet verisi geçersiz.', 'The summary data to send is invalid.') };
  }

  const { summaryEmailEnabled, summaryEmailEveryone } = await getEmailSettings();
  const toMe = force || summaryEmailEnabled;
  const everyone = !force && summaryEmailEveryone && isMeetingId(meetingId);
  if (!toMe && !everyone) {
    return { ok: false, reason: 'disabled', message: t('Otomatik özet e-postası kapalı.', 'Automatic summary email is off.') };
  }

  try {
    const res = await apiRequest<{ recipient: string; recipientCount: number }>('POST', '/api/v1/email/summary', {
      body: {
        meetingId: isMeetingId(meetingId) ? meetingId : undefined,
        toMe,
        everyone,
        language: uiLanguage(),
        content: safe,
      },
    });
    return { ok: true, recipient: res.recipient, recipientCount: res.recipientCount };
  } catch (err) {
    if (err instanceof ApiError) {
      // Aynı toplantının özeti katılımcılara zaten gitmiş (ör. önceki deneme
      // gönderdi ama not silinemedi) — tekrar gönderilmez, başarı sayılır.
      if (err.reason === 'already_sent') return { ok: true, recipient: '', recipientCount: 0 };
      if (err.reason === 'email_not_configured') {
        return {
          ok: false,
          reason: 'not_configured',
          message: t('E-posta gönderimi henüz açılmadı.', 'Email sending isn’t available yet.'),
        };
      }
      if (err.reason === 'no_recipient') {
        // Yalnızca "Herkese" açık ve adresi olan katılımcı yoksa paylaşılacak kimse yok — hata değil.
        if (!toMe) return { ok: false, reason: 'disabled', message: err.message };
        return { ok: false, reason: 'no_recipient', message: err.message };
      }
      const retryable = err.status === 0 || err.status === 429 || err.status >= 500;
      return { ok: false, reason: 'network', message: err.message, retryable };
    }
    console.error('[mailer] özet e-postası gönderilemedi:', err);
    return { ok: false, reason: 'network', message: t('E-posta gönderilemedi.', 'Could not send the email.'), retryable: true };
  }
}

/** Renderer'dan gelen yük güvenilmez: alanlar tek tek sınırlandırılıyor. */
function sanitizePayload(value: unknown): SummaryEmailPayload | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Partial<SummaryEmailPayload>;

  const text = (v: unknown, max: number): string =>
    typeof v === 'string' ? v.slice(0, max) : '';
  const list = (v: unknown, max: number, itemMax: number): string[] =>
    Array.isArray(v)
      ? v.filter((item): item is string => typeof item === 'string').slice(0, max).map((s) => s.slice(0, itemMax))
      : [];

  const title = text(input.title, 200).trim() || t('Toplantı', 'Meeting');

  return {
    title,
    recordedAtISO: text(input.recordedAtISO, 40) || new Date().toISOString(),
    durationLabel: text(input.durationLabel, 40),
    participants: list(input.participants, 40, 120),
    summary: {
      tldr: text(input.summary?.tldr, 4_000),
      keyPoints: list(input.summary?.keyPoints, 30, 600),
      decisions: list(input.summary?.decisions, 30, 600),
    },
    actionItems: Array.isArray(input.actionItems)
      ? input.actionItems
          .filter((item): item is SummaryEmailPayload['actionItems'][number] =>
            Boolean(item) && typeof (item as { text?: unknown }).text === 'string'
          )
          .slice(0, 60)
          .map((item) => ({
            text: item.text.slice(0, 600),
            ...(typeof item.assignee === 'string' ? { assignee: item.assignee.slice(0, 120) } : {}),
            completed: item.completed === true,
          }))
      : [],
  };
}
